import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import {
  FileText, Plus, Trash2, Copy, Download, Save, ArrowLeft, Eye, Edit, Search,
  Building2, Phone, Mail, Globe, Hash, User, Calendar, X, GripVertical, ChevronDown, ChevronUp, Settings2,
  ImagePlus, Link, ClipboardPaste, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const pdfErrorMessage = async (err) => {
  try {
    const text = await err?.response?.data?.text?.();
    const detail = text && JSON.parse(text).detail;
    if (detail) return typeof detail === 'string' ? detail : 'Błąd generowania PDF';
  } catch { /* non-JSON body */ }
  return `Błąd generowania PDF${err?.response?.status ? ` (kod ${err.response.status})` : ''}`;
};

const pdfFilename = (headers, fallback) => {
  const disposition = headers['content-disposition'] || '';
  const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) return decodeURIComponent(utf8[1]);
  const plain = disposition.match(/filename="?([^";]+)"?/);
  return plain ? plain[1] : fallback;
};


const STATUS_MAP = {
  draft: { label: 'Szkic', color: 'bg-gray-500' },
  sent: { label: 'Wysłana', color: 'bg-blue-500' },
  accepted: { label: 'Zaakceptowana', color: 'bg-green-500' },
  rejected: { label: 'Odrzucona', color: 'bg-red-500' },
};

const VAT_RATES = [0, 5, 8, 23];
const UNITS = ['szt', 'm²', 'mb', 'kpl', 'godz', 'usł'];

const emptyItem = () => ({
  _key: crypto.randomUUID(),
  name: '',
  description: '',
  type: 'product',
  quantity: 1,
  unit: 'szt',
  unit_price: 0,
  vat_rate: 23,
  photo_url: '',
});

// ============ ITEM PHOTO COMPONENT ============
const ItemPhoto = ({ photoUrl, onPhotoChange, isProduct }) => {
  const fileRef = useRef(null);
  const pasteZoneRef = useRef(null);
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);

  if (!isProduct) return null;

  const uploadFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) { toast.error('Wybierz plik obrazu'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/offers/item-photo`, fd);
      onPhotoChange(res.data.url);
      toast.success('Zdjęcie dodane');
    } catch { toast.error('Błąd przesyłania zdjęcia'); }
    finally { setUploading(false); }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) await uploadFile(file);
        return;
      }
    }
  };

  const handleUrlSubmit = async () => {
    if (!urlInput.trim()) return;
    setUploading(true);
    try {
      const res = await axios.post(`${API_URL}/offers/item-photo-url`, { url: urlInput.trim() });
      onPhotoChange(res.data.url);
      setUrlInput('');
      setShowUrlInput(false);
      toast.success('Zdjęcie pobrane i zapisane');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd pobierania zdjęcia z linku');
    } finally { setUploading(false); }
  };

  const fullUrl = photoUrl ? (photoUrl.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${photoUrl}` : photoUrl) : '';

  if (photoUrl) {
    return (
      <div className="relative group w-16 h-16 rounded-md overflow-hidden flex-shrink-0" style={{ border: '1px solid var(--crm-border)' }}>
        <img src={fullUrl} alt="Produkt" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-1 transition-opacity">
          <button onClick={() => fileRef.current?.click()} className="p-1 rounded bg-white/20 hover:bg-white/40" title="Zmień">
            <Upload className="w-3 h-3 text-white" />
          </button>
          <button onClick={() => onPhotoChange('')} className="p-1 rounded bg-white/20 hover:bg-white/40" title="Usuń">
            <Trash2 className="w-3 h-3 text-white" />
          </button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadFile(e.target.files[0])} />
      </div>
    );
  }

  return (
    <div ref={pasteZoneRef} onPaste={handlePaste} tabIndex={0}
      className="flex flex-col gap-1.5" data-testid="item-photo-zone">
      <div className="flex items-center gap-1.5 flex-wrap">
        <label className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] cursor-pointer transition-colors ${uploading ? 'opacity-50 pointer-events-none' : 'hover:bg-[var(--crm-bg-card-hover)]'}`}
          style={{ border: '1px dashed var(--crm-border)', color: 'var(--crm-text-muted)' }}>
          <Upload className="w-3 h-3" />
          <span>{uploading ? 'Wysyłanie...' : 'Dodaj zdjęcie'}</span>
          <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadFile(e.target.files[0])} data-testid="item-photo-file" />
        </label>
        <button onClick={() => pasteZoneRef.current?.focus()} title="Wklej ze schowka (Ctrl+V)"
          className="flex items-center gap-1 px-2 py-1 rounded text-[10px] transition-colors hover:bg-[var(--crm-bg-card-hover)]"
          style={{ border: '1px dashed var(--crm-border)', color: 'var(--crm-text-muted)' }}>
          <ClipboardPaste className="w-3 h-3" /> Wklej
        </button>
        <button onClick={() => setShowUrlInput(!showUrlInput)} title="Wklej link do zdjęcia"
          className="flex items-center gap-1 px-2 py-1 rounded text-[10px] transition-colors hover:bg-[var(--crm-bg-card-hover)]"
          style={{ border: '1px dashed var(--crm-border)', color: 'var(--crm-text-muted)' }}>
          <Link className="w-3 h-3" /> Link
        </button>
      </div>
      {showUrlInput && (
        <div className="flex gap-1">
          <Input value={urlInput} onChange={e => setUrlInput(e.target.value)} placeholder="https://... (adres obrazka)"
            className="h-7 text-xs flex-1" onKeyDown={e => e.key === 'Enter' && handleUrlSubmit()}
            style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
            data-testid="item-photo-url-input" />
          <Button size="sm" onClick={handleUrlSubmit} disabled={uploading || !urlInput.trim()}
            className="h-7 text-xs bg-[#FF6B35] hover:bg-[#e55a28] text-white" data-testid="item-photo-url-submit">
            {uploading ? '...' : 'Pobierz'}
          </Button>
        </div>
      )}
    </div>
  );
};

const calcTotals = (items) => {
  let totalNetto = 0;
  const vatBreakdown = {};
  for (const it of items) {
    const val = (it.quantity || 0) * (it.unit_price || 0);
    totalNetto += val;
    const rate = it.vat_rate ?? 23;
    vatBreakdown[rate] = (vatBreakdown[rate] || 0) + val * (rate / 100);
  }
  const totalVat = Object.values(vatBreakdown).reduce((s, v) => s + v, 0);
  return {
    total_netto: Math.round(totalNetto * 100) / 100,
    total_vat: Math.round(totalVat * 100) / 100,
    total_brutto: Math.round((totalNetto + totalVat) * 100) / 100,
    vat_breakdown: vatBreakdown,
  };
};

// ============ OFFER LIST VIEW ============
const OffersList = ({ offers, loading, onNew, onEdit, onDuplicate, onDelete, onPDF }) => {
  const [search, setSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const filtered = offers.filter(o =>
    !search || [o.number, o.client_name, o.offerer_name].some(f => (f || '').toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[var(--crm-text-primary)]" data-testid="offers-title">Konfigurator ofert</h1>
          <p className="text-sm text-[var(--crm-text-muted)] mt-1">Twórz i zarządzaj ofertami dla klientów</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={onNew} className="bg-[#FF6B35] hover:bg-[#e55a28] text-white" data-testid="new-offer-btn">
            <Plus className="w-4 h-4 mr-2" /> Nowa oferta
          </Button>
        </div>
      </div>

      <Card className="p-4" style={{ backgroundColor: 'var(--crm-bg-card)', border: '1px solid var(--crm-border)' }}>
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--crm-text-muted)]" />
          <Input
            placeholder="Szukaj po numerze, kliencie..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
            data-testid="offers-search"
          />
        </div>

        {loading ? (
          <div className="text-center py-12 text-[var(--crm-text-muted)]">Ładowanie ofert...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto text-[var(--crm-text-muted)] mb-3 opacity-40" />
            <p className="text-[var(--crm-text-muted)]">{search ? 'Brak wyników' : 'Brak ofert — utwórz pierwszą!'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ borderColor: 'var(--crm-border)' }}>
                  <TableHead className="text-[var(--crm-text-muted)]">Numer</TableHead>
                  <TableHead className="text-[var(--crm-text-muted)]">Klient</TableHead>
                  <TableHead className="text-[var(--crm-text-muted)]">Data</TableHead>
                  <TableHead className="text-[var(--crm-text-muted)] text-right">Brutto</TableHead>
                  <TableHead className="text-[var(--crm-text-muted)]">Status</TableHead>
                  <TableHead className="text-[var(--crm-text-muted)]">Autor</TableHead>
                  <TableHead className="text-[var(--crm-text-muted)] text-right">Akcje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((offer) => {
                  const st = STATUS_MAP[offer.status] || STATUS_MAP.draft;
                  return (
                    <TableRow key={offer.id} className="hover:bg-[var(--crm-bg-card-hover)] cursor-pointer" style={{ borderColor: 'var(--crm-border)' }}
                      onClick={() => onEdit(offer.id)} data-testid={`offer-row-${offer.id}`}>
                      <TableCell className="font-medium text-[var(--crm-text-primary)]">{offer.number}</TableCell>
                      <TableCell className="text-[var(--crm-text-primary)]">{offer.client_name || '—'}</TableCell>
                      <TableCell className="text-[var(--crm-text-secondary)]">{offer.date}</TableCell>
                      <TableCell className="text-right font-semibold text-[var(--crm-text-primary)]">{(offer.total_brutto || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</TableCell>
                      <TableCell><Badge className={`${st.color} text-white text-xs`}>{st.label}</Badge></TableCell>
                      <TableCell className="text-[var(--crm-text-secondary)]">{offer.created_by_initials}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => onPDF(offer.id)} title="Pobierz PDF" data-testid={`offer-pdf-${offer.id}`}>
                            <Download className="w-4 h-4 text-[var(--crm-text-secondary)]" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => onDuplicate(offer.id)} title="Duplikuj" data-testid={`offer-dup-${offer.id}`}>
                            <Copy className="w-4 h-4 text-[var(--crm-text-secondary)]" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(offer.id)} title="Usuń" data-testid={`offer-del-${offer.id}`}>
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent style={{ backgroundColor: 'var(--crm-bg-card)', borderColor: 'var(--crm-border)' }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[var(--crm-text-primary)]">Usunąć ofertę?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--crm-text-secondary)]">Ta operacja jest nieodwracalna.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ backgroundColor: 'var(--crm-bg-btn-secondary)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}>Anuluj</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={() => { onDelete(deleteConfirm); setDeleteConfirm(null); }}>Usuń</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ============ LIVE PDF PREVIEW ============
const LivePreview = ({ form, branding }) => {
  const totals = calcTotals(form.items || []);
  const validUntil = form.date && form.valid_days
    ? new Date(new Date(form.date).getTime() + form.valid_days * 86400000).toLocaleDateString('pl-PL')
    : '';

  return (
    <div className="bg-white text-gray-900 rounded-lg shadow-lg overflow-hidden relative" style={{ fontSize: '11px', lineHeight: '1.5' }} data-testid="offer-preview">
      {/* Watermark (center, faint) */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.04]" aria-hidden="true">
        <img src="/logo.png" alt="" className="w-64 brightness-0" />
      </div>

      {/* Header — logo top-left, matching letterhead */}
      <div className="p-5 pb-3">
        <img src="/logo.png" alt="Trendhomes" className="h-12 object-contain brightness-0 mb-4" style={{ filter: 'brightness(0) saturate(100%) invert(39%) sepia(91%) saturate(1000%) hue-rotate(358deg) brightness(101%) contrast(97%)' }} />
      </div>

      {/* Offer title — right-aligned */}
      <div className="px-5 pb-4">
        <div className="text-right">
          <p className="font-bold text-base text-[#333]">OFERTA {form.number}</p>
          <p className="text-gray-500 text-[10px]">Data: {form.date}{validUntil && ` | Ważna do: ${validUntil}`}</p>
        </div>
      </div>

      {/* Parties */}
      <div className="grid grid-cols-2 gap-4 px-5 pb-4">
        <div>
          <p className="font-semibold text-[10px] text-gray-400 uppercase mb-1">Klient</p>
          <p className="font-medium">{form.client_name || '—'}</p>
          {form.client_address && <p className="text-gray-600">{form.client_address}</p>}
          {form.client_phone && <p className="text-gray-600">Tel: {form.client_phone}</p>}
          {form.client_email && <p className="text-gray-600">{form.client_email}</p>}
        </div>
        <div>
          <p className="font-semibold text-[10px] text-gray-400 uppercase mb-1">Oferujący</p>
          <p className="font-medium">{form.offerer_name || '—'}</p>
          {form.offerer_email && <p className="text-gray-600">{form.offerer_email}</p>}
          {form.offerer_phone && <p className="text-gray-600">Tel: {form.offerer_phone}</p>}
        </div>
      </div>

      {/* Items Table */}
      {form.items && form.items.length > 0 && (
        <div className="px-5 pb-3">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="bg-[#FF6B35] text-white">
                <th className="p-1.5 text-left rounded-tl">Lp.</th>
                <th className="p-1.5 text-left">Nazwa</th>
                <th className="p-1.5 text-right">Ilość</th>
                <th className="p-1.5 text-left">Jedn.</th>
                <th className="p-1.5 text-right">Cena netto</th>
                <th className="p-1.5 text-right">VAT</th>
                <th className="p-1.5 text-right rounded-tr">Wartość netto</th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((item, i) => {
                const val = (item.quantity || 0) * (item.unit_price || 0);
                const photoSrc = item.photo_url ? (item.photo_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${item.photo_url}` : item.photo_url) : '';
                return (
                  <tr key={item._key} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-1.5 text-gray-500">{i + 1}</td>
                    <td className="p-1.5">
                      <div className="flex items-start gap-1.5">
                        {photoSrc && item.type === 'product' && (
                          <img src={photoSrc} alt="" className="w-8 h-8 object-cover rounded flex-shrink-0" />
                        )}
                        <div>
                          <span className="font-medium">{item.name || '—'}</span>
                          {item.description && <span className="block text-gray-400 text-[9px]">{item.description}</span>}
                        </div>
                      </div>
                    </td>
                    <td className="p-1.5 text-right">{item.quantity}</td>
                    <td className="p-1.5">{item.unit}</td>
                    <td className="p-1.5 text-right">{(item.unit_price || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</td>
                    <td className="p-1.5 text-right">{item.vat_rate}%</td>
                    <td className="p-1.5 text-right font-medium">{val.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary */}
      <div className="px-5 pb-4">
        <div className="flex justify-end">
          <div className="w-60 space-y-1 text-[11px]">
            <div className="flex justify-between">
              <span className="text-gray-500">Suma netto:</span>
              <span className="font-medium">{totals.total_netto.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</span>
            </div>
            {Object.entries(totals.vat_breakdown).map(([rate, amount]) => (
              <div key={rate} className="flex justify-between">
                <span className="text-gray-500">VAT {rate}%:</span>
                <span>{amount.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</span>
              </div>
            ))}
            <div className="flex justify-between border-t-2 border-[#FF6B35] pt-1 mt-1">
              <span className="font-bold">SUMA BRUTTO:</span>
              <span className="font-bold text-sm">{totals.total_brutto.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</span>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      {form.notes && (
        <div className="px-5 pb-4">
          <p className="font-semibold text-[10px] text-gray-400 uppercase mb-1">Uwagi / Warunki</p>
          <p className="text-gray-600 whitespace-pre-line text-[10px]">{form.notes}</p>
        </div>
      )}

      {/* Footer */}
      <div className="mt-auto px-5 pt-3 pb-3">
        <div className="flex items-start justify-between text-[9px] text-gray-500">
          <div className="pr-4">
            <p className="font-bold text-[#C5184E]">{branding.company_name || 'TRENDHOMES Janusz Romanek'}</p>
            <p>{branding.address || 'ul. Piastowska 3'}, {branding.address_city || '38-500 Sanok'}</p>
            {branding.nip && <p>NIP: {branding.nip}</p>}
          </div>
          <div className="px-4">
            <p className="font-bold text-[#C5184E]">Godziny otwarcia</p>
            <p>Poniedziałek — Piątek: 8:00 — 17:00</p>
            <p>{branding.phone || '+48 735 721 222'}, {branding.email || 'kontakt@trendhomes.pl'}</p>
          </div>
          <div className="text-right">
            <a href="https://trendhomes.pl" target="_blank" rel="noopener noreferrer"
              className="text-sm hover:underline cursor-pointer">
              www.<span className="font-bold text-[#C5184E]">trendhomes</span>.pl
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ OFFER EDITOR ============
const OfferEditor = ({ offerId, onBack, branding, onBrandingOpen }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    number: '',
    date: new Date().toISOString().split('T')[0],
    valid_days: 30,
    status: 'draft',
    client_name: '',
    client_address: '',
    client_phone: '',
    client_email: '',
    offerer_name: branding.offerer_person || branding.company_name || user?.initials || '',
    offerer_email: branding.email || user?.email || '',
    offerer_phone: branding.phone || '',
    items: [emptyItem()],
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!offerId);
  const [showPreview, setShowPreview] = useState(false);
  const formRef = useRef(null);

  // Client autocomplete
  const [clientResults, setClientResults] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientRef = useRef(null);
  const searchTimeout = useRef(null);

  const searchClients = useCallback(async (q) => {
    if (q.length < 2) { setClientResults([]); setShowClientDropdown(false); return; }
    try {
      const res = await axios.get(`${API_URL}/clients/search`, { params: { q } });
      setClientResults(res.data);
      setShowClientDropdown(res.data.length > 0);
    } catch { setClientResults([]); }
  }, []);

  const handleClientNameChange = useCallback((val) => {
    setForm(prev => ({ ...prev, client_name: val }));
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchClients(val), 300);
  }, [searchClients]);

  const selectClient = useCallback((c) => {
    setForm(prev => ({
      ...prev,
      client_name: c.nazwa,
      client_address: c.adres || prev.client_address,
      client_phone: c.telefon || prev.client_phone,
      client_email: c.email || prev.client_email,
    }));
    setShowClientDropdown(false);
    setClientResults([]);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (clientRef.current && !clientRef.current.contains(e.target)) setShowClientDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!offerId) return;
    axios.get(`${API_URL}/offers/${offerId}`).then(res => {
      const data = res.data;
      // Add stable keys to items
      if (data.items) {
        data.items = data.items.map(it => ({ ...it, _key: it._key || crypto.randomUUID() }));
      }
      setForm(data);
    }).catch(() => toast.error('Nie udało się wczytać oferty'))
      .finally(() => setLoading(false));
  }, [offerId]);

  const updateField = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateItem = useCallback((idx, field, value) => {
    setForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  }, []);

  const addItem = useCallback(() => {
    setForm(prev => ({ ...prev, items: [...prev.items, emptyItem()] }));
  }, []);

  const removeItem = useCallback((idx) => {
    setForm(prev => {
      if (prev.items.length <= 1) return prev;
      return { ...prev, items: prev.items.filter((_, i) => i !== idx) };
    });
  }, []);

  const moveItem = useCallback((idx, dir) => {
    setForm(prev => {
      const items = [...prev.items];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= items.length) return prev;
      [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
      return { ...prev, items };
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const totals = calcTotals(form.items || []);
      // Strip _key before sending
      const itemsClean = (form.items || []).map(({ _key, ...rest }) => rest);
      const payload = { ...form, ...totals, items: itemsClean };
      delete payload._key;
      delete payload._id;

      if (offerId) {
        await axios.put(`${API_URL}/offers/${offerId}`, payload);
      } else {
        const res = await axios.post(`${API_URL}/offers`, payload);
        // Update URL without reload — set offerId so future saves use PUT
        window.history.replaceState(null, '', `/konfigurator`);
        setForm(prev => ({ ...prev, id: res.data.id, number: res.data.number }));
      }
      toast.success('Oferta zapisana');
      onBack();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd zapisu');
    } finally {
      setSaving(false);
    }
  };

  const handlePDF = async () => {
    if (!offerId) {
      toast.error('Najpierw zapisz ofertę');
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/offers/${offerId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = pdfFilename(res.headers, `Oferta_${form.number || 'draft'}.pdf`);
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(await pdfErrorMessage(err));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-[var(--crm-text-muted)]">Ładowanie oferty...</div>;
  }

  const totals = calcTotals(form.items || []);

  return (
    <div className="space-y-4" ref={formRef}>
      {/* Top bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack} data-testid="back-to-list">
            <ArrowLeft className="w-4 h-4 mr-1 text-[var(--crm-text-secondary)]" />
            <span className="text-[var(--crm-text-secondary)]">Lista</span>
          </Button>
          <h1 className="text-xl font-bold text-[var(--crm-text-primary)]">
            {offerId ? `Edycja: ${form.number}` : 'Nowa oferta'}
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={onBrandingOpen} style={{ borderColor: 'var(--crm-border)', color: 'var(--crm-text-secondary)' }} data-testid="branding-btn">
            <Settings2 className="w-4 h-4 mr-1" /> Branding
          </Button>
          <Button variant="outline" className="md:hidden" onClick={() => setShowPreview(!showPreview)}
            style={{ borderColor: 'var(--crm-border)', color: 'var(--crm-text-secondary)' }}>
            <Eye className="w-4 h-4 mr-1" /> {showPreview ? 'Formularz' : 'Podgląd'}
          </Button>
          {offerId && (
            <Button variant="outline" onClick={handlePDF} style={{ borderColor: 'var(--crm-border)', color: 'var(--crm-text-secondary)' }} data-testid="download-pdf-btn">
              <Download className="w-4 h-4 mr-1" /> PDF
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} className="bg-[#FF6B35] hover:bg-[#e55a28] text-white" data-testid="save-offer-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Zapisywanie...' : 'Zapisz'}
          </Button>
        </div>
      </div>

      {/* 2-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEFT: Form (hidden on mobile when preview is shown) */}
        <div className={`space-y-4 ${showPreview ? 'hidden md:block' : ''}`}>
          {/* Header info */}
          <Card className="p-4 space-y-3" style={{ backgroundColor: 'var(--crm-bg-card)', border: '1px solid var(--crm-border)' }}>
            <h2 className="text-sm font-semibold text-[var(--crm-text-primary)] uppercase tracking-wider">Dane oferty</h2>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Numer</label>
                <Input value={form.number} onChange={e => updateField('number', e.target.value)} placeholder="Auto"
                  style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                  data-testid="offer-number" />
              </div>
              <div>
                <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Data</label>
                <Input type="date" value={form.date} onChange={e => updateField('date', e.target.value)}
                  style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                  data-testid="offer-date" />
              </div>
              <div>
                <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Ważność (dni)</label>
                <Input type="number" value={form.valid_days} onChange={e => updateField('valid_days', parseInt(e.target.value) || 0)}
                  style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                  data-testid="offer-valid-days" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Status</label>
              <Select value={form.status} onValueChange={v => updateField('status', v)}>
                <SelectTrigger style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }} data-testid="offer-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: 'var(--crm-bg-card)', borderColor: 'var(--crm-border)' }}>
                  {Object.entries(STATUS_MAP).map(([k, v]) => (
                    <SelectItem key={k} value={k} className="text-[var(--crm-text-primary)]">{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </Card>

          {/* Client — with autocomplete from CRM clients */}
          <Card className="p-4 space-y-3" style={{ backgroundColor: 'var(--crm-bg-card)', border: '1px solid var(--crm-border)' }}>
            <h2 className="text-sm font-semibold text-[var(--crm-text-primary)] uppercase tracking-wider">Klient</h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 sm:col-span-1 relative" ref={clientRef}>
                <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Nazwa / Imię i nazwisko</label>
                <Input value={form.client_name} onChange={e => handleClientNameChange(e.target.value)}
                  placeholder="Zacznij pisać, żeby wyszukać klienta..."
                  style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                  data-testid="client-name" />
                {showClientDropdown && clientResults.length > 0 && (
                  <div className="absolute z-20 top-full left-0 right-0 mt-1 rounded-md shadow-lg max-h-48 overflow-y-auto"
                    style={{ backgroundColor: 'var(--crm-bg-card)', border: '1px solid var(--crm-border)' }}
                    data-testid="client-autocomplete-dropdown">
                    {clientResults.map((c) => (
                      <button key={c.id} type="button" onClick={() => selectClient(c)}
                        className="w-full text-left px-3 py-2 hover:bg-[var(--crm-bg-card-hover)] text-sm transition-colors"
                        style={{ color: 'var(--crm-text-primary)' }}>
                        <span className="font-medium">{c.nazwa}</span>
                        {c.telefon && <span className="text-[var(--crm-text-muted)] ml-2">Tel: {c.telefon}</span>}
                        {c.adres && <span className="block text-xs text-[var(--crm-text-muted)]">{c.adres}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Telefon</label>
                <Input value={form.client_phone} onChange={e => updateField('client_phone', e.target.value)}
                  placeholder="+48 600 000 000" style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                  data-testid="client-phone" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Adres</label>
                <Input value={form.client_address} onChange={e => updateField('client_address', e.target.value)}
                  placeholder="ul. Przykładowa 1, 00-001 Warszawa" style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                  data-testid="client-address" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Email</label>
                <Input value={form.client_email} onChange={e => updateField('client_email', e.target.value)}
                  placeholder="jan@example.com" style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                  data-testid="client-email" />
              </div>
            </div>
          </Card>

          {/* Offerer */}
          <Card className="p-4 space-y-3" style={{ backgroundColor: 'var(--crm-bg-card)', border: '1px solid var(--crm-border)' }}>
            <h2 className="text-sm font-semibold text-[var(--crm-text-primary)] uppercase tracking-wider">Oferujący</h2>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Imię / Inicjały</label>
                <Input value={form.offerer_name} onChange={e => updateField('offerer_name', e.target.value)}
                  style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                  data-testid="offerer-name" />
              </div>
              <div>
                <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Email</label>
                <Input value={form.offerer_email} onChange={e => updateField('offerer_email', e.target.value)}
                  style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                  data-testid="offerer-email" />
              </div>
              <div>
                <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Telefon</label>
                <Input value={form.offerer_phone} onChange={e => updateField('offerer_phone', e.target.value)}
                  style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                  data-testid="offerer-phone" />
              </div>
            </div>
          </Card>

          {/* Items */}
          <Card className="p-4 space-y-3" style={{ backgroundColor: 'var(--crm-bg-card)', border: '1px solid var(--crm-border)' }}>
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-[var(--crm-text-primary)] uppercase tracking-wider">Pozycje oferty</h2>
              <Button size="sm" onClick={addItem} className="bg-[#FF6B35] hover:bg-[#e55a28] text-white h-8" data-testid="add-item-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Dodaj
              </Button>
            </div>

            <div className="space-y-3">
              {form.items.map((item, idx) => (
                <div key={item._key} className="rounded-lg p-3 space-y-2" style={{ backgroundColor: 'var(--crm-bg-main)', border: '1px solid var(--crm-border)' }} data-testid={`item-row-${idx}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-bold text-[var(--crm-text-muted)] w-6">{idx + 1}.</span>
                      <div className="flex gap-0.5">
                        <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="p-0.5 rounded hover:bg-[var(--crm-bg-card-hover)] disabled:opacity-30">
                          <ChevronUp className="w-3.5 h-3.5 text-[var(--crm-text-muted)]" />
                        </button>
                        <button onClick={() => moveItem(idx, 1)} disabled={idx === form.items.length - 1} className="p-0.5 rounded hover:bg-[var(--crm-bg-card-hover)] disabled:opacity-30">
                          <ChevronDown className="w-3.5 h-3.5 text-[var(--crm-text-muted)]" />
                        </button>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} disabled={form.items.length <= 1} className="h-7 w-7 p-0" data-testid={`remove-item-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="sm:col-span-2">
                      <Input placeholder="Nazwa produktu / usługi" value={item.name}
                        onChange={e => updateItem(idx, 'name', e.target.value)}
                        style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                        data-testid={`item-name-${idx}`} />
                    </div>
                    <div className="sm:col-span-2">
                      <Input placeholder="Opis (opcjonalnie)" value={item.description}
                        onChange={e => updateItem(idx, 'description', e.target.value)}
                        style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                        data-testid={`item-desc-${idx}`} />
                    </div>
                  </div>

                  {/* Photo for product items */}
                  <ItemPhoto
                    photoUrl={item.photo_url || ''}
                    onPhotoChange={(url) => updateItem(idx, 'photo_url', url)}
                    isProduct={item.type === 'product'}
                  />

                  <div className="grid grid-cols-5 gap-2">
                    <div>
                      <label className="text-[10px] text-[var(--crm-text-muted)]">Typ</label>
                      <Select value={item.type} onValueChange={v => updateItem(idx, 'type', v)}>
                        <SelectTrigger className="h-8 text-xs" style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent style={{ backgroundColor: 'var(--crm-bg-card)', borderColor: 'var(--crm-border)' }}>
                          <SelectItem value="product" className="text-[var(--crm-text-primary)]">Produkt</SelectItem>
                          <SelectItem value="service" className="text-[var(--crm-text-primary)]">Usługa</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] text-[var(--crm-text-muted)]">Ilość</label>
                      <Input type="number" min={0} step="0.01" value={item.quantity}
                        onChange={e => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        className="h-8 text-xs"
                        style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                        data-testid={`item-qty-${idx}`} />
                    </div>
                    <div>
                      <label className="text-[10px] text-[var(--crm-text-muted)]">Jedn.</label>
                      <Select value={item.unit} onValueChange={v => updateItem(idx, 'unit', v)}>
                        <SelectTrigger className="h-8 text-xs" style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent style={{ backgroundColor: 'var(--crm-bg-card)', borderColor: 'var(--crm-border)' }}>
                          {UNITS.map(u => <SelectItem key={u} value={u} className="text-[var(--crm-text-primary)]">{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] text-[var(--crm-text-muted)]">Cena netto</label>
                      <Input type="number" min={0} step="0.01" value={item.unit_price}
                        onChange={e => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                        className="h-8 text-xs"
                        style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                        data-testid={`item-price-${idx}`} />
                    </div>
                    <div>
                      <label className="text-[10px] text-[var(--crm-text-muted)]">VAT</label>
                      <Select value={String(item.vat_rate)} onValueChange={v => updateItem(idx, 'vat_rate', parseInt(v))}>
                        <SelectTrigger className="h-8 text-xs" style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent style={{ backgroundColor: 'var(--crm-bg-card)', borderColor: 'var(--crm-border)' }}>
                          {VAT_RATES.map(r => <SelectItem key={r} value={String(r)} className="text-[var(--crm-text-primary)]">{r}%</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="text-right text-xs text-[var(--crm-text-secondary)]">
                    Wartość netto: <span className="font-semibold text-[var(--crm-text-primary)]">
                      {((item.quantity || 0) * (item.unit_price || 0)).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Totals summary */}
            <div className="pt-3 border-t" style={{ borderColor: 'var(--crm-border)' }}>
              <div className="flex justify-end">
                <div className="w-56 space-y-1 text-sm">
                  <div className="flex justify-between text-[var(--crm-text-secondary)]">
                    <span>Netto:</span>
                    <span className="text-[var(--crm-text-primary)]">{totals.total_netto.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</span>
                  </div>
                  <div className="flex justify-between text-[var(--crm-text-secondary)]">
                    <span>VAT:</span>
                    <span className="text-[var(--crm-text-primary)]">{totals.total_vat.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</span>
                  </div>
                  <div className="flex justify-between font-bold text-[var(--crm-text-primary)] border-t pt-1" style={{ borderColor: 'var(--crm-border)' }}>
                    <span>Brutto:</span>
                    <span className="text-[#FF6B35]">{totals.total_brutto.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Notes */}
          <Card className="p-4 space-y-3" style={{ backgroundColor: 'var(--crm-bg-card)', border: '1px solid var(--crm-border)' }}>
            <h2 className="text-sm font-semibold text-[var(--crm-text-primary)] uppercase tracking-wider">Uwagi / Warunki</h2>
            <Textarea value={form.notes} onChange={e => updateField('notes', e.target.value)}
              placeholder="Dodaj warunki oferty, terminy, informacje o gwarancji..."
              rows={4}
              style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
              data-testid="offer-notes" />
          </Card>
        </div>

        {/* RIGHT: Live Preview */}
        <div className={`${showPreview ? '' : 'hidden md:block'}`}>
          <div className="sticky top-4">
            <div className="mb-2 flex items-center gap-2">
              <Eye className="w-4 h-4 text-[var(--crm-text-muted)]" />
              <span className="text-xs font-semibold text-[var(--crm-text-muted)] uppercase tracking-wider">Podgląd na żywo</span>
            </div>
            <div className="max-h-[calc(100vh-140px)] overflow-y-auto rounded-lg" style={{ border: '1px solid var(--crm-border)' }}>
              <LivePreview form={{ ...form, ...totals }} branding={branding} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ BRANDING MODAL ============
const BrandingModal = ({ open, onClose, branding, onSave }) => {
  const [form, setForm] = useState(branding);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(branding);
  }, [branding, open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings/offer-branding`, form);
      onSave(form);
      toast.success('Branding zapisany');
      onClose();
    } catch {
      toast.error('Błąd zapisu brandingu');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/settings/offer-branding/logo`, fd);
      setForm(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success('Logo przesłane');
    } catch {
      toast.error('Błąd przesyłania logo');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-full max-w-lg mx-4 rounded-xl p-6 shadow-2xl" onClick={e => e.stopPropagation()}
        style={{ backgroundColor: 'var(--crm-bg-card)', border: '1px solid var(--crm-border)' }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[var(--crm-text-primary)]">Branding firmy</h2>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-[var(--crm-bg-card-hover)]">
            <X className="w-5 h-5 text-[var(--crm-text-secondary)]" />
          </button>
        </div>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div>
            <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Logo firmy</label>
            <div className="flex items-center gap-3">
              {form.logo_url && (
                <img src={form.logo_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${form.logo_url}` : form.logo_url}
                  alt="Logo" className="h-10 object-contain rounded" />
              )}
              <label className="cursor-pointer text-xs text-[#FF6B35] hover:underline">
                {form.logo_url ? 'Zmień logo' : 'Wgraj logo'}
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="branding-logo-input" />
              </label>
            </div>
          </div>
          <div>
            <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Nazwa firmy</label>
            <Input value={form.company_name || ''} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))}
              style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
              data-testid="branding-company" />
          </div>
          <div>
            <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Imię i nazwisko oferującego</label>
            <Input value={form.offerer_person || ''} onChange={e => setForm(p => ({ ...p, offerer_person: e.target.value }))}
              placeholder="Jan Kowalski"
              style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
              data-testid="branding-offerer-person" />
          </div>
          <div>
            <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Adres (ulica)</label>
            <Input value={form.address || ''} onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
              placeholder="ul. gen. Leopolda Okulickiego 8"
              style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
              data-testid="branding-address" />
          </div>
          <div>
            <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Kod pocztowy i miasto</label>
            <Input value={form.address_city || ''} onChange={e => setForm(p => ({ ...p, address_city: e.target.value }))}
              placeholder="38-500 Sanok"
              style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
              data-testid="branding-address-city" />
          </div>
          <div>
            <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Salon sprzedaży (adres)</label>
            <Input value={form.salon_address || ''} onChange={e => setForm(p => ({ ...p, salon_address: e.target.value }))}
              placeholder="38-500 Sanok, ul. Piastowska 3"
              style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
              data-testid="branding-salon" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Telefon</label>
              <Input value={form.phone || ''} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                data-testid="branding-phone" />
            </div>
            <div>
              <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Email</label>
              <Input value={form.email || ''} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                data-testid="branding-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">NIP</label>
              <Input value={form.nip || ''} onChange={e => setForm(p => ({ ...p, nip: e.target.value }))}
                style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                data-testid="branding-nip" />
            </div>
            <div>
              <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Strona www</label>
              <Input value={form.website || ''} onChange={e => setForm(p => ({ ...p, website: e.target.value }))}
                style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
                data-testid="branding-website" />
            </div>
          </div>
          <div>
            <label className="text-xs text-[var(--crm-text-muted)] mb-1 block">Tekst stopki (na każdej stronie PDF)</label>
            <Textarea value={form.footer_text || ''} onChange={e => setForm(p => ({ ...p, footer_text: e.target.value }))} rows={2}
              style={{ backgroundColor: 'var(--crm-bg-input)', color: 'var(--crm-text-primary)', borderColor: 'var(--crm-border)' }}
              data-testid="branding-footer" />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-3 border-t" style={{ borderColor: 'var(--crm-border)' }}>
          <Button variant="outline" onClick={onClose} style={{ borderColor: 'var(--crm-border)', color: 'var(--crm-text-secondary)' }}>Anuluj</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#FF6B35] hover:bg-[#e55a28] text-white" data-testid="save-branding-btn">
            {saving ? 'Zapisywanie...' : 'Zapisz branding'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============ MAIN PAGE ============
export default function OfferConfigurator() {
  const [view, setView] = useState('list'); // 'list' | 'edit'
  const [editId, setEditId] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branding, setBranding] = useState({
    company_name: 'TRENDHOMES Janusz Romanek', address: 'ul. Piastowska 3',
    address_city: '38-500 Sanok', salon_address: '38-500 Sanok, ul. Piastowska 3',
    phone: '+48 735 721 222', email: 'kontakt@trendhomes.pl', website: 'www.trendhomes.pl',
    nip: '6871226807', footer_text: '', logo_url: '', offerer_person: '',
  });
  const [brandingOpen, setBrandingOpen] = useState(false);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/offers`);
      setOffers(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const fetchBranding = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/settings/offer-branding`);
      setBranding(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchOffers();
    fetchBranding();
  }, [fetchOffers, fetchBranding]);

  const handleNew = () => { setEditId(null); setView('edit'); };
  const handleEdit = (id) => { setEditId(id); setView('edit'); };
  const handleBack = () => { setView('list'); setEditId(null); fetchOffers(); };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API_URL}/offers/${id}/duplicate`);
      toast.success('Oferta zduplikowana');
      fetchOffers();
    } catch { toast.error('Błąd duplikowania'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/offers/${id}`);
      toast.success('Oferta usunięta');
      fetchOffers();
    } catch { toast.error('Błąd usuwania'); }
  };

  const handlePDF = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/offers/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = pdfFilename(res.headers, 'Oferta.pdf');
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(await pdfErrorMessage(err)); }
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
        {view === 'list' ? (
          <OffersList
            offers={offers}
            loading={loading}
            onNew={handleNew}
            onEdit={handleEdit}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
            onPDF={handlePDF}
          />
        ) : (
          <OfferEditor
            offerId={editId}
            onBack={handleBack}
            branding={branding}
            onBrandingOpen={() => setBrandingOpen(true)}
          />
        )}

        <BrandingModal
          open={brandingOpen}
          onClose={() => setBrandingOpen(false)}
          branding={branding}
          onSave={(updated) => setBranding(updated)}
        />
      </div>
    </Layout>
  );
}
